import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AddSpeakerModal } from "@/components/AddSpeakerModal";
import { Badge } from "@/components/ui/badge";
import { Plus, Mail, Phone, Edit, Trash2 } from "lucide-react";
import { type Speaker } from "@shared/schema";

export default function Speakers() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const { data: speakers = [], isLoading } = useQuery<Speaker[]>({
    queryKey: ["/api/speakers"],
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getColorClass = (index: number) => {
    const colors = [
      { bg: "bg-primary/10", text: "text-primary" },
      { bg: "bg-secondary/10", text: "text-secondary" },
      { bg: "bg-accent/10", text: "text-accent" },
      { bg: "bg-destructive/10", text: "text-destructive" },
    ];
    return colors[index % colors.length];
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-muted-foreground">Manage your church speakers and pastors</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
          data-testid="button-add-speaker"
        >
          <Plus className="w-5 h-5" />
          Add Speaker
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {speakers.map((speaker, index) => {
          const colorClass = getColorClass(index);
          
          return (
            <Card key={speaker.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`card-speaker-${speaker.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-16 h-16 ${colorClass.bg} rounded-full flex items-center justify-center text-2xl font-bold ${colorClass.text}`}>
                    {getInitials(speaker.firstName, speaker.lastName)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg" data-testid={`text-speaker-name-${speaker.id}`}>
                      {speaker.firstName} {speaker.lastName}
                    </h4>
                    <p className="text-sm text-muted-foreground">{speaker.title}</p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  {speaker.email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span data-testid={`text-speaker-email-${speaker.id}`}>{speaker.email}</span>
                    </div>
                  )}
                  {speaker.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span data-testid={`text-speaker-phone-${speaker.id}`}>{speaker.phone}</span>
                    </div>
                  )}
                </div>
                
                {speaker.bio && (
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">{speaker.bio}</p>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    data-testid={`button-edit-${speaker.id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    data-testid={`button-delete-${speaker.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {speakers.length === 0 && (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No speakers yet</h3>
                <p className="text-muted-foreground mb-4">Add your first speaker to get started with stream management.</p>
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  className="btn-primary"
                  data-testid="button-add-first-speaker"
                >
                  Add Speaker
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <AddSpeakerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  );
}
